export type NotificationTypeId =
  | 'radio-live'
  | 'programa-iniciado'
  | 'locutor-live'
  | 'nuevo-concurso'
  | 'nuevo-evento'
  | 'nuevo-ganador'
  | 'nuevo-comentario'
  | 'nuevo-seguidor'
  | 'invitacion-club';

export type NotificationCategoryId =
  | 'actividad'
  | 'social'
  | 'concursos'
  | 'eventos'
  | 'clubes'
  | 'sistema';

export type NotificationActionId = 'ver' | 'leido' | 'eliminar' | 'configurar';

export interface NotificationType {
  id: NotificationTypeId;
  name: string;
  icon: string;
  description: string;
  category: NotificationCategoryId;
  color: string;
  badgeColor: string;
  priority: 'normal' | 'alta' | 'urgente';
}

export interface NotificationCategory {
  id: NotificationCategoryId;
  name: string;
  icon: string;
  color: string;
  badgeColor: string;
  notifTypes: NotificationTypeId[];
  totalCount: number;
  unreadCount: number;
}

export interface NotificationItem {
  id: number;
  type: NotificationTypeId;
  title: string;
  message: string;
  preview: string;
  thumbnailUrl: string;
  iconUrl: string;
  timestamp: string;
  timestampRelative: string;
  isRead: boolean;
  isPinned: boolean;
  actionUrl: string;
  actionLabel: string;
  sourceName: string;
  sourceFreq: string;
  category: NotificationCategoryId;
  priority: 'normal' | 'alta' | 'urgente';
  canMarkRead: boolean;
  canDelete: boolean;
  metadata: string;
}

export interface NotificationDashboardStats {
  totalNotificaciones: number;
  totalHoy: number;
  totalSinLeer: number;
  masFrecuente: string;
  masFrecuenteCount: number;
  tasaApertura: number;
  tiempoPromedioAccion: string;
  notificacionesPorDia: number;
}

export interface NotificationActionsInfo {
  id: NotificationActionId;
  name: string;
  icon: string;
  description: string;
  color: string;
  shortcut: string;
}

export const notificationTypes: NotificationType[] = [
  {
    id: 'radio-live',
    name: 'Radio En Vivo',
    icon: 'ri-broadcast-line',
    description: 'Cuando una de tus radios favoritas comienza a transmitir en vivo.',
    category: 'actividad',
    color: 'text-rose-600',
    badgeColor: 'bg-rose-500',
    priority: 'alta',
  },
  {
    id: 'programa-iniciado',
    name: 'Programa Iniciado',
    icon: 'ri-timer-line',
    description: 'Cuando un programa que seguís está por comenzar o ya arrancó.',
    category: 'actividad',
    color: 'text-emerald-600',
    badgeColor: 'bg-emerald-500',
    priority: 'alta',
  },
  {
    id: 'locutor-live',
    name: 'Locutor En Vivo',
    icon: 'ri-user-voice-line',
    description: 'Cuando un locutor que seguís sale al aire.',
    category: 'actividad',
    color: 'text-amber-600',
    badgeColor: 'bg-amber-500',
    priority: 'normal',
  },
  {
    id: 'nuevo-concurso',
    name: 'Nuevo Concurso',
    icon: 'ri-trophy-line',
    description: 'Cuando se publica un concurso nuevo en el que podés participar.',
    category: 'concursos',
    color: 'text-orange-600',
    badgeColor: 'bg-orange-500',
    priority: 'alta',
  },
  {
    id: 'nuevo-evento',
    name: 'Nuevo Evento',
    icon: 'ri-calendar-event-line',
    description: 'Cuando una radio que seguís crea un nuevo evento.',
    category: 'eventos',
    color: 'text-violet-600',
    badgeColor: 'bg-violet-500',
    priority: 'normal',
  },
  {
    id: 'nuevo-ganador',
    name: 'Nuevo Ganador',
    icon: 'ri-medal-line',
    description: 'Cuando se anuncian los ganadores de un concurso.',
    category: 'concursos',
    color: 'text-yellow-600',
    badgeColor: 'bg-yellow-500',
    priority: 'alta',
  },
  {
    id: 'nuevo-comentario',
    name: 'Nuevo Comentario',
    icon: 'ri-chat-3-line',
    description: 'Cuando alguien comenta en una publicación o te responde.',
    category: 'social',
    color: 'text-sky-600',
    badgeColor: 'bg-sky-500',
    priority: 'normal',
  },
  {
    id: 'nuevo-seguidor',
    name: 'Nuevo Seguidor',
    icon: 'ri-user-add-line',
    description: 'Cuando alguien comienza a seguirte.',
    category: 'social',
    color: 'text-teal-600',
    badgeColor: 'bg-teal-500',
    priority: 'normal',
  },
  {
    id: 'invitacion-club',
    name: 'Invitación Club',
    icon: 'ri-group-line',
    description: 'Cuando recibís una invitación para unirte a un club de fans.',
    category: 'clubes',
    color: 'text-indigo-600',
    badgeColor: 'bg-indigo-500',
    priority: 'alta',
  },
];

export const notificationCategories: NotificationCategory[] = [
  {
    id: 'actividad',
    name: 'Actividad',
    icon: 'ri-flashlight-line',
    color: 'text-rose-600',
    badgeColor: 'bg-rose-500',
    notifTypes: ['radio-live', 'programa-iniciado', 'locutor-live'],
    totalCount: 14200,
    unreadCount: 230,
  },
  {
    id: 'social',
    name: 'Social',
    icon: 'ri-user-heart-line',
    color: 'text-teal-600',
    badgeColor: 'bg-teal-500',
    notifTypes: ['nuevo-comentario', 'nuevo-seguidor'],
    totalCount: 21800,
    unreadCount: 420,
  },
  {
    id: 'concursos',
    name: 'Concursos',
    icon: 'ri-trophy-line',
    color: 'text-orange-600',
    badgeColor: 'bg-orange-500',
    notifTypes: ['nuevo-concurso', 'nuevo-ganador'],
    totalCount: 8900,
    unreadCount: 145,
  },
  {
    id: 'eventos',
    name: 'Eventos',
    icon: 'ri-calendar-event-line',
    color: 'text-violet-600',
    badgeColor: 'bg-violet-500',
    notifTypes: ['nuevo-evento'],
    totalCount: 11200,
    unreadCount: 180,
  },
  {
    id: 'clubes',
    name: 'Clubes',
    icon: 'ri-group-line',
    color: 'text-indigo-600',
    badgeColor: 'bg-indigo-500',
    notifTypes: ['invitacion-club'],
    totalCount: 5600,
    unreadCount: 85,
  },
  {
    id: 'sistema',
    name: 'Sistema',
    icon: 'ri-settings-3-line',
    color: 'text-foreground-600',
    badgeColor: 'bg-foreground-500',
    notifTypes: [],
    totalCount: 3400,
    unreadCount: 50,
  },
];

export const notificationItems: NotificationItem[] = [
  {
    id: 1,
    type: 'radio-live',
    title: 'La Mexicana FM está al aire',
    message: 'Alejandro Ramírez inició "El Sabor del Mediodía". Conectate ahora y participá en vivo mandando tus saludos desde la cabina.',
    preview: 'La Mexicana FM comenzó su transmisión en vivo con El Sabor del Mediodía. Mandá tu saludo.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Mexican%20radio%20station%20live%20broadcast%20studio%20with%20warm%20lighting%20professional%20microphone%20setup%20broadcast%20equipment%20modern%20studio%20environment%20energetic%20atmosphere%20clean%20aesthetic%20professional%20photography&width=800&height=450&seq=notif-ctr-live-1&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20with%20guitar%20and%20sombrero%20elements%20circular%20design%20warm%20orange%20and%20green%20colors%20clean%20modern%20broadcast%20style%20festive%20vibrant%20feel%20isolated%20on%20transparent%20background%20vector%20flat%20design&width=120&height=120&seq=notif-ctr-icon-lm&orientation=squarish',
    timestamp: '2026-06-21T12:00:00',
    timestampRelative: 'Ahora mismo',
    isRead: false,
    isPinned: false,
    actionUrl: '/radio/la-mexicana-fm',
    actionLabel: 'Escuchar en vivo',
    sourceName: 'La Mexicana FM',
    sourceFreq: '98.7 FM',
    category: 'actividad',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: '2,450 oyentes conectados',
  },
  {
    id: 2,
    type: 'nuevo-comentario',
    title: 'María López comentó tu publicación',
    message: '"¡Excelente análisis! Justo estaba pensando lo mismo sobre la nueva programación. Me encantó el punto 3 sobre la interacción con la audiencia."',
    preview: 'María López respondió a tu post: "¡Excelente análisis!..."',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Friendly%20latina%20woman%20mid%20twenties%20casual%20style%20warm%20smile%20professional%20portrait%20photography%20soft%20warm%20neutral%20background%20subtle%20golden%20glow%20clean%20editorial%20quality&width=800&height=450&seq=notif-ctr-avatar-maria&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Friendly%20latina%20woman%20profile%20photo%20casual%20professional%20warm%20natural%20lighting%20half%20body%20portrait%20clean%20background%20editorial%20quality&width=120&height=120&seq=notif-ctr-icon-maria&orientation=squarish',
    timestamp: '2026-06-21T11:45:00',
    timestampRelative: 'Hace 15 minutos',
    isRead: false,
    isPinned: false,
    actionUrl: '/listener',
    actionLabel: 'Ver comentario',
    sourceName: 'María López',
    sourceFreq: '',
    category: 'social',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: 'En tu post "Análisis de la radio 2026"',
  },
  {
    id: 3,
    type: 'nuevo-concurso',
    title: 'Concurso: Ganá entradas para el RadioFest 2026',
    message: 'Radio Éxito FM lanzó un concurso donde podés ganar 2 entradas VIP para el RadioFest. Solo tenés que grabar un audio de 30 segundos contando por qué amás la radio.',
    preview: 'Radio Éxito FM sortea entradas VIP para el RadioFest 2026. Participá ahora.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Exciting%20radio%20contest%20announcement%20with%20golden%20tickets%20and%20radio%20microphone%20on%20stage%20dramatic%20spotlight%20atmosphere%20concert%20venue%20golden%20hour%20lighting%20professional%20event%20photography%20winner%20celebration&width=800&height=450&seq=notif-ctr-thumb-concurso&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20bright%20energetic%20warm%20orange%20and%20yellow%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=notif-ctr-icon-exito&orientation=squarish',
    timestamp: '2026-06-21T10:30:00',
    timestampRelative: 'Hace 1 hora',
    isRead: false,
    isPinned: true,
    actionUrl: '/competencias',
    actionLabel: 'Participar ahora',
    sourceName: 'Radio Éxito FM',
    sourceFreq: '98.5 FM',
    category: 'concursos',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: 'Cierra en 3 días · 850 participantes',
  },
  {
    id: 4,
    type: 'nuevo-seguidor',
    title: 'Carlos Vega comenzó a seguirte',
    message: 'Carlos Vega, oyente frecuente de Radio Peninsular, ahora te sigue. Tenés 340 seguidores en total. ¡Tu comunidad sigue creciendo!',
    preview: 'Carlos Vega te sigue ahora. Ya son 340 seguidores.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Friendly%20latino%20man%20mid%20twenties%20casual%20style%20warm%20genuine%20smile%20professional%20portrait%20photography%20clean%20white%20background%20soft%20natural%20lighting%20editorial%20quality&width=800&height=450&seq=notif-ctr-avatar-carlos&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Friendly%20latino%20man%20profile%20photo%20casual%20professional%20warm%20natural%20lighting%20half%20body%20portrait%20clean%20background%20editorial%20quality&width=120&height=120&seq=notif-ctr-icon-carlos&orientation=squarish',
    timestamp: '2026-06-21T09:15:00',
    timestampRelative: 'Hace 2 horas',
    isRead: false,
    isPinned: false,
    actionUrl: '/seguidores',
    actionLabel: 'Ver perfil',
    sourceName: 'Carlos Vega',
    sourceFreq: '',
    category: 'social',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: '340 seguidores totales',
  },
  {
    id: 5,
    type: 'programa-iniciado',
    title: 'El Despertador está comenzando',
    message: 'En 10 minutos arranca "El Despertador" edición 500 en Radio Éxito FM. Hoy con invitado especial: Ricky Navarro, la voz revelación del año.',
    preview: 'El Despertador edición 500 comienza en 10 min en Radio Éxito FM.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Morning%20radio%20show%20studio%20golden%20hour%20sunlight%20through%20windows%20hosts%20preparing%20for%20broadcast%20warm%20energetic%20atmosphere%20professional%20radio%20studio%20with%20microphones%20coffee%20cups%20scripts&width=800&height=450&seq=notif-ctr-thumb-despertador&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20bright%20energetic%20warm%20orange%20and%20yellow%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=notif-ctr-icon-exito2&orientation=squarish',
    timestamp: '2026-06-21T08:50:00',
    timestampRelative: 'Hace 3 horas',
    isRead: true,
    isPinned: false,
    actionUrl: '/radio/radio-exito-fm',
    actionLabel: 'Sintonizar',
    sourceName: 'Radio Éxito FM',
    sourceFreq: '98.5 FM',
    category: 'actividad',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: 'Edición especial 500 · Invitado Ricky Navarro',
  },
  {
    id: 6,
    type: 'nuevo-evento',
    title: 'Nuevo evento: Noche de Jazz bajo las Estrellas',
    message: 'Estéreo Capital organiza una noche especial de jazz en vivo en la terraza del Centro Cultural. Capacidad limitada a 200 personas. Reservá tu lugar.',
    preview: 'Estéreo Capital presenta Noche de Jazz bajo las Estrellas este viernes.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Elegant%20rooftop%20jazz%20event%20at%20night%20with%20string%20lights%20live%20musicians%20on%20stage%20intimate%20atmosphere%20city%20skyline%20in%20background%20warm%20amber%20lighting%20sophisticated%20ambiance%20professional%20event%20photography&width=800&height=450&seq=notif-ctr-thumb-jazz&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20warm%20earthy%20tones%20professional%20broadcasting%20emblem%20contemporary%20minimalist%20design&width=120&height=120&seq=notif-ctr-icon-capital&orientation=squarish',
    timestamp: '2026-06-20T18:30:00',
    timestampRelative: 'Ayer',
    isRead: true,
    isPinned: false,
    actionUrl: '/eventos',
    actionLabel: 'Ver evento',
    sourceName: 'Estéreo Capital',
    sourceFreq: '102.1 FM',
    category: 'eventos',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: 'Viernes 27 de junio · 20:00 · Centro Cultural',
  },
  {
    id: 7,
    type: 'invitacion-club',
    title: 'Invitación al Club de Fans de La Mexicana FM',
    message: 'Has sido invitado al Club Oficial de Fans de La Mexicana FM. Como miembro tendrás acceso a contenido exclusivo, saludos al aire prioritarios y meet & greets con los locutores.',
    preview: 'Te invitaron al Club Oficial de Fans de La Mexicana FM. ¡Unite ahora!',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Passionate%20radio%20fan%20club%20gathering%20with%20branded%20merchandise%20cheering%20and%20celebrating%20together%20warm%20community%20atmosphere%20colorful%20banners%20Mexican%20music%20vibes%20professional%20event%20photography%20enthusiastic%20crowd&width=800&height=450&seq=notif-ctr-thumb-club&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20with%20guitar%20and%20sombrero%20elements%20circular%20design%20warm%20orange%20and%20green%20colors%20clean%20modern%20broadcast%20style%20festive%20vibrant%20feel%20isolated%20on%20transparent%20background%20vector%20flat%20design&width=120&height=120&seq=notif-ctr-icon-lm2&orientation=squarish',
    timestamp: '2026-06-20T15:00:00',
    timestampRelative: 'Ayer',
    isRead: false,
    isPinned: false,
    actionUrl: '/clubes',
    actionLabel: 'Unirme al club',
    sourceName: 'La Mexicana FM',
    sourceFreq: '98.7 FM',
    category: 'clubes',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: '2,300 miembros · Club Oro',
  },
  {
    id: 8,
    type: 'nuevo-ganador',
    title: '¡Ganadores del concurso "La Voz de tu Ciudad"!',
    message: 'Ya están los resultados. Felicitaciones a Andrea Martínez, ganadora del primer lugar. Su audio recibió 12,400 votos del público. ¡Escuchalo ahora!',
    preview: 'Andrea Martínez ganó La Voz de tu Ciudad con 12,400 votos.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Excited%20girl%20holding%20golden%20microphone%20trophy%20on%20stage%20confetti%20falling%20dramatic%20spotlight%20celebration%20moment%20achievement%20proud%20expression%20professional%20event%20photography%20winner%20moment&width=800&height=450&seq=notif-ctr-thumb-ganador&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20youthful%20energetic%20neon%20pink%20and%20cyan%20accents%20modern%20design%20professional%20broadcasting%20emblem&width=120&height=120&seq=notif-ctr-icon-juvenil&orientation=squarish',
    timestamp: '2026-06-20T14:00:00',
    timestampRelative: 'Ayer',
    isRead: true,
    isPinned: false,
    actionUrl: '/ganadores',
    actionLabel: 'Ver ganadores',
    sourceName: 'Radio Juvenil FM',
    sourceFreq: '105.3 FM',
    category: 'concursos',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: '3 ganadores · 12,400 votos',
  },
  {
    id: 9,
    type: 'locutor-live',
    title: 'Valentina Ríos está al aire',
    message: 'La reina de la tarde arrancó su programa en Estéreo Capital. Hoy con una playlist especial de los 90s que no te podés perder.',
    preview: 'Valentina Ríos comenzó su programa en Estéreo Capital. ¡Sintonizala!',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Confident%20female%20radio%20host%20in%20elegant%20modern%20studio%20setting%20warm%20smile%20professional%20casual%20chic%20outfit%20natural%20lighting%20premium%20portrait%20photography%20sophisticated%20atmosphere&width=800&height=450&seq=notif-ctr-thumb-valentina&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20warm%20earthy%20tones%20professional%20broadcasting%20emblem%20contemporary%20minimalist%20design&width=120&height=120&seq=notif-ctr-icon-capital2&orientation=squarish',
    timestamp: '2026-06-20T12:00:00',
    timestampRelative: 'Ayer',
    isRead: true,
    isPinned: false,
    actionUrl: '/radio/estereo-capital',
    actionLabel: 'Escuchar ahora',
    sourceName: 'Estéreo Capital',
    sourceFreq: '102.1 FM',
    category: 'actividad',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: '1,890 oyentes conectados',
  },
  {
    id: 10,
    type: 'nuevo-comentario',
    title: 'Locutor respondió tu mensaje en Cabina',
    message: 'Ricky Navarro leyó tu saludo al aire en El Despertador: "¡Saludos a Marco desde Houston! Gracias por empezar la mañana con nosotros, compa. ¡Arriba el ánimo!"',
    preview: 'Ricky Navarro leyó tu saludo al aire: "¡Saludos a Marco desde Houston!..."',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20male%20radio%20host%20in%20modern%20studio%20holding%20headphones%20warm%20genuine%20smile%20responding%20to%20listener%20message%20professional%20broadcast%20environment%20golden%20hour%20lighting%20through%20window&width=800&height=450&seq=notif-ctr-thumb-ricky&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20bright%20energetic%20warm%20orange%20and%20yellow%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=notif-ctr-icon-exito3&orientation=squarish',
    timestamp: '2026-06-20T09:00:00',
    timestampRelative: 'Ayer',
    isRead: false,
    isPinned: false,
    actionUrl: '/listener',
    actionLabel: 'Ver mensaje',
    sourceName: 'Ricky Navarro',
    sourceFreq: '98.5 FM',
    category: 'social',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: 'En respuesta a tu saludo en cabina',
  },
  {
    id: 11,
    type: 'nuevo-concurso',
    title: 'Trivia Express: Ganá $500 en efectivo',
    message: 'Radio Peninsular lanzó Trivia Express. 5 preguntas, 60 segundos. El primero en responder todas correctamente se lleva $500. ¿Te animás?',
    preview: 'Trivia Express en Radio Peninsular: $500 en efectivo al ganador.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Fast%20paced%20radio%20trivia%20game%20show%20concept%20with%20countdown%20timer%20bright%20neon%20lighting%20buzzers%20and%20prize%20money%20envelope%20dramatic%20energetic%20atmosphere%20game%20show%20photography&width=800&height=450&seq=notif-ctr-thumb-trivia&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20coastal%20inspired%20blue%20and%20warm%20sand%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=notif-ctr-icon-peninsular&orientation=squarish',
    timestamp: '2026-06-19T16:30:00',
    timestampRelative: 'Hace 2 días',
    isRead: false,
    isPinned: true,
    actionUrl: '/trivias',
    actionLabel: 'Jugar ahora',
    sourceName: 'Radio Peninsular',
    sourceFreq: '101.9 FM',
    category: 'concursos',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: 'Premio: $500 · Abierto ahora',
  },
  {
    id: 12,
    type: 'nuevo-evento',
    title: 'Masterclass gratuita: Cómo crear tu propio podcast',
    message: 'Radio Industria presenta una masterclass online gratuita sobre producción de podcasts. Aprendé desde cero con expertos de la industria. Cupos limitados.',
    preview: 'Masterclass gratuita de producción de podcasts por Radio Industria.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Professional%20podcast%20production%20workshop%20with%20instructor%20presenting%20to%20engaged%20audience%20modern%20studio%20setup%20with%20microphones%20audio%20equipment%20warm%20educational%20atmosphere%20professional%20photography%20clean%20composition&width=800&height=450&seq=notif-ctr-thumb-masterclass&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20industrial%20modern%20style%20metallic%20warm%20tones%20professional%20broadcasting%20emblem&width=120&height=120&seq=notif-ctr-icon-industria&orientation=squarish',
    timestamp: '2026-06-19T14:00:00',
    timestampRelative: 'Hace 2 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/eventos',
    actionLabel: 'Inscribirme',
    sourceName: 'Radio Industria',
    sourceFreq: '93.5 FM',
    category: 'eventos',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: 'Sábado 28 junio · 10:00 AM · Online',
  },
  {
    id: 13,
    type: 'nuevo-seguidor',
    title: '4 personas comenzaron a seguirte',
    message: 'Andrea Ruiz, Diego Torres, Laura Sánchez y Miguel Ángel ahora te siguen. ¡Seguí compartiendo contenido de calidad!',
    preview: 'Andrea Ruiz y 3 personas más te siguen. Ya son 344 seguidores.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Group%20of%20four%20diverse%20friendly%20people%20smiling%20warm%20casual%20style%20clean%20modern%20portrait%20photography%20diverse%20ethnicities%20professional%20headshots%20montage%20bright%20studio%20lighting&width=800&height=450&seq=notif-ctr-avatar-grupo&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Diverse%20group%20of%20four%20friendly%20people%20collage%20circular%20avatars%20warm%20casual%20style%20professional%20photography%20clean%20bright%20studio%20lighting&width=120&height=120&seq=notif-ctr-icon-grupo&orientation=squarish',
    timestamp: '2026-06-19T11:00:00',
    timestampRelative: 'Hace 2 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/seguidores',
    actionLabel: 'Ver seguidores',
    sourceName: 'Andrea Ruiz',
    sourceFreq: '',
    category: 'social',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: '344 seguidores totales',
  },
  {
    id: 14,
    type: 'invitacion-club',
    title: 'Club de Fans de Radio Peninsular: membresía VIP',
    message: 'Por tu nivel de actividad, calificás para la membresía VIP del Club de Fans de Radio Peninsular. Beneficios: meet & greets, contenido exclusivo y tu propio perfil destacado.',
    preview: 'Membresía VIP disponible en el Club de Fans de Radio Peninsular.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Exclusive%20VIP%20radio%20fan%20club%20lounge%20with%20premium%20seating%20branded%20decor%20golden%20ambient%20lighting%20warm%20welcoming%20atmosphere%20professional%20event%20photography%20luxury%20casual%20vibe&width=800&height=450&seq=notif-ctr-thumb-vipclub&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20coastal%20inspired%20blue%20and%20warm%20sand%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=notif-ctr-icon-peninsular2&orientation=squarish',
    timestamp: '2026-06-19T09:00:00',
    timestampRelative: 'Hace 2 días',
    isRead: false,
    isPinned: false,
    actionUrl: '/clubes',
    actionLabel: 'Ver beneficios',
    sourceName: 'Radio Peninsular',
    sourceFreq: '101.9 FM',
    category: 'clubes',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: 'Membresía VIP · 5 beneficios exclusivos',
  },
  {
    id: 15,
    type: 'radio-live',
    title: 'Rock Total Radio está transmitiendo en vivo',
    message: 'Arrancó "Sábados de Rock" con lo mejor del rock clásico, nacional e internacional. Hoy especial: Los 100 mejores solos de guitarra de la historia.',
    preview: 'Rock Total Radio en vivo: Sábados de Rock, especial 100 solos.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Energetic%20rock%20radio%20studio%20with%20electric%20guitars%20on%20wall%20vintage%20band%20posters%20vibrant%20red%20and%20black%20ambiance%20passionate%20hosts%20at%20microphones%20professional%20broadcast%20equipment%20concert%20lighting%20feel&width=800&height=450&seq=notif-ctr-thumb-rock&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20with%20electric%20guitar%20and%20lightning%20bolt%20circular%20design%20black%20and%20red%20color%20scheme%20modern%20rock%20broadcast%20emblem%20energetic%20and%20bold%20style%20isolated%20on%20transparent%20background%20vector%20flat%20design&width=120&height=120&seq=notif-ctr-icon-rock&orientation=squarish',
    timestamp: '2026-06-18T16:00:00',
    timestampRelative: 'Hace 3 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/radio/rock-total-radio',
    actionLabel: 'Escuchar en vivo',
    sourceName: 'Rock Total Radio',
    sourceFreq: '95.1 FM',
    category: 'actividad',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: '3,200 oyentes conectados',
  },
  {
    id: 16,
    type: 'nuevo-ganador',
    title: 'Ganadores de la Copa RFW de Locución 2026',
    message: 'Resultados oficiales: 1er lugar Javier Mendoza (Deportes), 2do Valentina Ríos (Entretenimiento), 3ro Ricky Navarro (Revelación). ¡Qué nivel!',
    preview: 'Javier Mendoza ganó la Copa RFW de Locución 2026 en la categoría Deportes.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Three%20radio%20hosts%20holding%20golden%20trophies%20on%20awards%20stage%20dramatic%20amber%20spotlight%20confetti%20celebration%20professional%20event%20photography%20proud%20expressions%20achievement%20moment%20elegant%20venue&width=800&height=450&seq=notif-ctr-thumb-copa&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Official%20RFW%20golden%20trophy%20logo%20professional%20modern%20circular%20design%20prestigious%20award%20emblem%20gold%20and%20white%20tones&width=120&height=120&seq=notif-ctr-icon-rfw&orientation=squarish',
    timestamp: '2026-06-18T20:00:00',
    timestampRelative: 'Hace 3 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/ganadores',
    actionLabel: 'Ver todos los ganadores',
    sourceName: 'RFW Oficial',
    sourceFreq: 'Global',
    category: 'concursos',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: '3 ganadores · 4 categorías · 128 participantes',
  },
  {
    id: 17,
    type: 'nuevo-comentario',
    title: 'Nuevo comentario en tu video',
    message: '"Increíble la calidad del sonido en esta transmisión. ¿Qué micrófono usan? Me encantaría saber para mejorar mi setup casero. ¡Saludos desde Argentina!" — Pablo Fernández',
    preview: 'Pablo Fernández comentó en tu video de backstage.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Friendly%20Argentine%20man%20early%20thirties%20casual%20professional%20style%20warm%20smile%20portrait%20photography%20soft%20natural%20lighting%20clean%20background%20editorial%20quality&width=800&height=450&seq=notif-ctr-avatar-pablo&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Friendly%20Argentine%20man%20profile%20photo%20casual%20professional%20warm%20natural%20lighting%20half%20body%20portrait%20clean%20background%20editorial%20quality&width=120&height=120&seq=notif-ctr-icon-pablo&orientation=squarish',
    timestamp: '2026-06-18T14:30:00',
    timestampRelative: 'Hace 3 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/videos',
    actionLabel: 'Responder',
    sourceName: 'Pablo Fernández',
    sourceFreq: '',
    category: 'social',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: 'En tu video "Backstage Copa RFW"',
  },
  {
    id: 18,
    type: 'radio-live',
    title: 'Radio Fe y Esperanza comenzó transmisión',
    message: 'El Pastor Daniel inició "Mañanas de Fe" con el mensaje del día. Un espacio de reflexión y música espiritual para arrancar la semana con buena energía.',
    preview: 'Radio Fe y Esperanza al aire con Mañanas de Fe.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Peaceful%20Christian%20radio%20studio%20with%20warm%20morning%20light%20cross%20and%20dove%20symbols%20gentle%20ambiance%20pastor%20at%20microphone%20sincere%20expression%20spiritual%20atmosphere%20soft%20natural%20lighting%20through%20window&width=800&height=450&seq=notif-ctr-thumb-fe&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Christian%20radio%20station%20logo%20with%20dove%20symbol%20circular%20minimalist%20design%20soft%20warm%20colors%20white%20and%20gentle%20golden%20tones%20clean%20modern%20broadcast%20emblem%20spiritual%20community%20radio%20isolated%20on%20transparent%20background%20vector%20style%20flat%20design&width=120&height=120&seq=notif-ctr-icon-fe&orientation=squarish',
    timestamp: '2026-06-18T08:00:00',
    timestampRelative: 'Hace 3 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/radio/radio-fe-y-esperanza',
    actionLabel: 'Sintonizar',
    sourceName: 'Radio Fe y Esperanza',
    sourceFreq: '91.5 FM',
    category: 'actividad',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: '1,560 oyentes conectados',
  },
  {
    id: 19,
    type: 'programa-iniciado',
    title: 'Deportes Total arranca cobertura del partido',
    message: 'Comienza la transmisión del clásico nacional. Javier Mendoza y todo el equipo de Deportes Total con la previa, el minuto a minuto y el análisis post-partido.',
    preview: 'Deportes Total inicia transmisión del clásico nacional.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20sports%20radio%20broadcast%20studio%20multiple%20screens%20showing%20live%20match%20passionate%20hosts%20in%20discussion%20sporty%20atmosphere%20professional%20broadcast%20equipment%20energetic%20dramatic%20red%20and%20dark%20lighting&width=800&height=450&seq=notif-ctr-thumb-deportes&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20trophy%20icon%20circular%20design%20dark%20blue%20and%20gold%20color%20scheme%20dynamic%20broadcast%20emblem%20energetic%20sports%20feel%20isolated%20on%20transparent%20background%20vector%20flat%20design&width=120&height=120&seq=notif-ctr-icon-deportes&orientation=squarish',
    timestamp: '2026-06-17T18:00:00',
    timestampRelative: 'Hace 4 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/radio/radio-deportiva-total',
    actionLabel: 'Escuchar partido',
    sourceName: 'Radio Deportiva Total',
    sourceFreq: '95.7 FM',
    category: 'actividad',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: '8,900 oyentes · Clásico nacional',
  },
  {
    id: 20,
    type: 'nuevo-evento',
    title: 'Encuentro de Radios Comunitarias — Agenda confirmada',
    message: 'Ya está disponible la agenda completa del Encuentro de Radios Comunitarias. 12 charlas, 8 talleres y 5 paneles con speakers de todo el continente.',
    preview: 'Agenda confirmada para el Encuentro de Radios Comunitarias.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Community%20radio%20gathering%20agenda%20board%20with%20colorful%20schedule%20diverse%20group%20of%20broadcasters%20reviewing%20program%20warm%20inclusive%20atmosphere%20cultural%20center%20natural%20daylight%20documentary%20photography&width=800&height=450&seq=notif-ctr-thumb-comunitario&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Official%20RFW%20community%20logo%20professional%20modern%20circular%20design%20green%20and%20earth%20tones%20broadcasting%20emblem%20inclusive%20style&width=120&height=120&seq=notif-ctr-icon-rfw2&orientation=squarish',
    timestamp: '2026-06-17T12:00:00',
    timestampRelative: 'Hace 4 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/eventos',
    actionLabel: 'Ver agenda completa',
    sourceName: 'RFW Oficial',
    sourceFreq: 'Global',
    category: 'eventos',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: 'Septiembre 5 · Centro Cultural · 200 asistentes',
  },
  {
    id: 21,
    type: 'nuevo-seguidor',
    title: 'Sofía Quintero comenzó a seguirte',
    message: 'La locutora de las madrugadas en Radio Éxito FM ahora te sigue. ¡Qué honor! Ella tiene 15,400 seguidores y es una de las voces más queridas de la radio.',
    preview: 'Sofía Quintero, locutora de Radio Éxito FM, te sigue ahora.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Calm%20female%20radio%20host%20in%20quiet%20studio%20with%20warm%20gentle%20lighting%20soft%20expression%20professional%20casual%20look%20peaceful%20morning%20atmosphere%20premium%20portrait%20photography%20serene%20vibe&width=800&height=450&seq=notif-ctr-avatar-sofia&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Calm%20female%20radio%20host%20profile%20photo%20serene%20expression%20professional%20style%20warm%20natural%20lighting%20half%20body%20portrait%20clean%20background%20editorial%20quality&width=120&height=120&seq=notif-ctr-icon-sofia&orientation=squarish',
    timestamp: '2026-06-17T06:00:00',
    timestampRelative: 'Hace 4 días',
    isRead: false,
    isPinned: false,
    actionUrl: '/seguidores',
    actionLabel: 'Ver perfil de Sofía',
    sourceName: 'Sofía Quintero',
    sourceFreq: '98.5 FM',
    category: 'social',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: '15,400 seguidores · Locutora destacada',
  },
  {
    id: 22,
    type: 'invitacion-club',
    title: 'Invitación al Club de Fans de Estéreo Capital',
    message: 'Por tu participación activa en la comunidad, el Club de Fans de Estéreo Capital te invita a unirte. Disfrutá de descuentos en eventos, contenido detrás de escena y más.',
    preview: 'Invitación al Club de Fans de Estéreo Capital.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Sophisticated%20radio%20fan%20club%20lounge%20warm%20earthy%20tones%20elegant%20ambiance%20members%20socializing%20branded%20environment%20premium%20coffee%20table%20setup%20professional%20photography%20inviting%20atmosphere&width=800&height=450&seq=notif-ctr-thumb-club-capital&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20warm%20earthy%20tones%20professional%20broadcasting%20emblem%20contemporary%20minimalist%20design&width=120&height=120&seq=notif-ctr-icon-capital3&orientation=squarish',
    timestamp: '2026-06-16T16:00:00',
    timestampRelative: 'Hace 5 días',
    isRead: false,
    isPinned: false,
    actionUrl: '/clubes',
    actionLabel: 'Ver club',
    sourceName: 'Estéreo Capital',
    sourceFreq: '102.1 FM',
    category: 'clubes',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: '3,800 miembros · Club Diamante',
  },
  {
    id: 23,
    type: 'nuevo-concurso',
    title: 'Concurso de Jingles: $2,000 y tu creación al aire',
    message: 'Radio Juvenil FM busca el próximo jingle oficial de la emisora. Grabá tu propuesta de 15 segundos y participá. El ganador se lleva $2,000 y su jingle sonará todo el año.',
    preview: 'Radio Juvenil FM busca su nuevo jingle. Premio: $2,000.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Music%20producer%20creating%20radio%20jingle%20in%20creative%20home%20studio%20with%20MIDI%20keyboard%20colorful%20audio%20waveforms%20on%20screen%20energetic%20creative%20atmosphere%20young%20dynamic%20vibe%20professional%20photography%20bright%20lighting&width=800&height=450&seq=notif-ctr-thumb-jingle&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20youthful%20energetic%20neon%20pink%20and%20cyan%20accents%20modern%20design%20professional%20broadcasting%20emblem&width=120&height=120&seq=notif-ctr-icon-juvenil2&orientation=squarish',
    timestamp: '2026-06-16T14:00:00',
    timestampRelative: 'Hace 5 días',
    isRead: true,
    isPinned: true,
    actionUrl: '/competencias',
    actionLabel: 'Participar',
    sourceName: 'Radio Juvenil FM',
    sourceFreq: '105.3 FM',
    category: 'concursos',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: 'Premio: $2,000 · Cierra en 2 semanas',
  },
  {
    id: 24,
    type: 'locutor-live',
    title: 'Javier Mendoza está narrando en vivo',
    message: 'El legendario narrador deportivo está al aire con la transmisión del partido de semifinales. No te pierdas su análisis en vivo con ese estilo único que lo hizo leyenda.',
    preview: 'Javier Mendoza narrando en vivo las semifinales.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Experienced%20male%20sports%20radio%20commentator%20in%20dynamic%20broadcast%20booth%20passionate%20expression%20headset%20on%20surrounded%20by%20screens%20showing%20live%20match%20warm%20professional%20lighting%20veteran%20presence%20intense%20moment&width=800&height=450&seq=notif-ctr-thumb-javier&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20trophy%20icon%20circular%20design%20dark%20blue%20and%20gold%20color%20scheme%20dynamic%20broadcast%20emblem%20energetic%20sports%20feel%20isolated%20on%20transparent%20background%20vector%20flat%20design&width=120&height=120&seq=notif-ctr-icon-deportes2&orientation=squarish',
    timestamp: '2026-06-16T20:00:00',
    timestampRelative: 'Hace 5 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/radio/radio-deportiva-total',
    actionLabel: 'Escuchar narración',
    sourceName: 'Radio Deportiva Total',
    sourceFreq: '95.7 FM',
    category: 'actividad',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: '12,300 oyentes · Semifinal',
  },
  {
    id: 25,
    type: 'nuevo-ganador',
    title: 'Resultados del Reto 30 Días de Contenido',
    message: '¡Finalizó el reto! Más de 200 radios participaron. Los ganadores: 1ro Radio Peninsular (30/30 días), 2do Radio Éxito FM (29/30), 3ro Estéreo Capital (28/30).',
    preview: 'Radio Peninsular ganó el Reto 30 Días de Contenido.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Three%20radio%20station%20representatives%20holding%20achievement%20certificates%20on%20stage%20celebration%20moment%20confetti%20and%20applause%20warm%20golden%20lighting%20professional%20event%20photography%20proud%20expressions%20achievement&width=800&height=450&seq=notif-ctr-thumb-reto&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Official%20RFW%20golden%20trophy%20logo%20professional%20modern%20circular%20design%20prestigious%20award%20emblem%20gold%20and%20white%20tones&width=120&height=120&seq=notif-ctr-icon-rfw3&orientation=squarish',
    timestamp: '2026-06-15T18:00:00',
    timestampRelative: 'Hace 6 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/ganadores',
    actionLabel: 'Ver resultados',
    sourceName: 'RFW Oficial',
    sourceFreq: 'Global',
    category: 'concursos',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: '200+ radios · 3 ganadores · Premios semanales',
  },
  {
    id: 26,
    type: 'nuevo-evento',
    title: 'RadioFest 2026 — Preventa de entradas ya disponible',
    message: '¡La preventa exclusiva para miembros RFW ya está abierta! 30% de descuento en todas las categorías. El festival de radios más grande de Latinoamérica te espera.',
    preview: 'Preventa RadioFest 2026: 30% off para miembros RFW.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Massive%20outdoor%20music%20festival%20RadioFest%202026%20promotional%20poster%20with%20multiple%20stages%20vibrant%20crowd%20colorful%20stage%20lighting%20spectacular%20atmosphere%20golden%20hour%20wide%20panoramic%20shot%20professional%20photography&width=800&height=450&seq=notif-ctr-thumb-radiofest&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Official%20RFW%20festival%20logo%20professional%20modern%20circular%20design%20vibrant%20colorful%20accents%20festive%20atmosphere%20premier%20event%20branding&width=120&height=120&seq=notif-ctr-icon-rfw4&orientation=squarish',
    timestamp: '2026-06-15T10:00:00',
    timestampRelative: 'Hace 6 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/eventos',
    actionLabel: 'Comprar entradas',
    sourceName: 'RFW Oficial',
    sourceFreq: 'Global',
    category: 'eventos',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: '30% descuento · Solo miembros · Preventa limitada',
  },
  {
    id: 27,
    type: 'nuevo-comentario',
    title: '5 comentarios nuevos en tu post',
    message: 'Tu publicación "Guía de monetización para radios" está generando mucho debate. Los últimos comentarios son muy positivos. ¡Seguí compartiendo contenido de valor!',
    preview: '5 nuevos comentarios en tu guía de monetización.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Engaged%20online%20community%20discussion%20with%20multiple%20comment%20bubbles%20floating%20around%20a%20popular%20post%20warm%20digital%20environment%20vibrant%20interaction%20concept%20modern%20social%20media%20aesthetic%20professional%20photography&width=800&height=450&seq=notif-ctr-thumb-comentarios&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Multiple%20colorful%20user%20avatar%20icons%20circular%20design%20diverse%20community%20warm%20tones%20professional%20photography%20collage%20style&width=120&height=120&seq=notif-ctr-icon-multi&orientation=squarish',
    timestamp: '2026-06-15T09:00:00',
    timestampRelative: 'Hace 6 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/listener',
    actionLabel: 'Ver comentarios',
    sourceName: '5 oyentes',
    sourceFreq: '',
    category: 'social',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: '89 comentarios en total · 234 likes',
  },
  {
    id: 28,
    type: 'nuevo-seguidor',
    title: 'Ricky Navarro comenzó a seguirte',
    message: 'La voz revelación del año, Ricky Navarro de Radio Élite Digital, ahora te sigue. Tu contenido está llamando la atención de los grandes.',
    preview: 'Ricky Navarro, locutor revelación, te sigue ahora.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20young%20male%20radio%20host%20with%20warm%20smile%20casual%20professional%20style%20golden%20portrait%20lighting%20friendly%20approachable%20vibe%20premium%20photography%20clean%20background&width=800&height=450&seq=notif-ctr-avatar-ricky&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20young%20male%20radio%20host%20profile%20photo%20warm%20smile%20professional%20style%20golden%20lighting%20half%20body%20portrait%20clean%20background%20editorial%20quality&width=120&height=120&seq=notif-ctr-icon-ricky&orientation=squarish',
    timestamp: '2026-06-15T07:30:00',
    timestampRelative: 'Hace 6 días',
    isRead: false,
    isPinned: false,
    actionUrl: '/seguidores',
    actionLabel: 'Ver perfil',
    sourceName: 'Ricky Navarro',
    sourceFreq: '99.1 FM',
    category: 'social',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: '22,100 seguidores · Locutor revelación',
  },
  {
    id: 29,
    type: 'invitacion-club',
    title: 'Invitación al Club VIP RFW',
    message: '¡Felicitaciones! Por tu nivel de actividad y contribuciones, has sido seleccionado para unirte al exclusivo Club VIP RFW. Accedé a beneficios premium, eventos privados y networking con las estrellas de la radio.',
    preview: 'Has sido seleccionado para el Club VIP RFW. Beneficios premium.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Exclusive%20VIP%20club%20lounge%20luxury%20setting%20with%20golden%20accents%20premium%20furniture%20sophisticated%20ambiance%20champagne%20glasses%20warm%20lighting%20professional%20photography%20elite%20atmosphere%20members%20area&width=800&height=450&seq=notif-ctr-thumb-viprfw&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Official%20RFW%20VIP%20logo%20professional%20modern%20circular%20design%20golden%20accents%20luxurious%20branding%20exclusive%20emblem%20premium%20style&width=120&height=120&seq=notif-ctr-icon-rfwvip&orientation=squarish',
    timestamp: '2026-06-14T20:00:00',
    timestampRelative: 'Hace 1 semana',
    isRead: false,
    isPinned: true,
    actionUrl: '/vip-rfw',
    actionLabel: 'Unirme al Club VIP',
    sourceName: 'RFW Oficial',
    sourceFreq: 'Global',
    category: 'clubes',
    priority: 'urgente',
    canMarkRead: true,
    canDelete: true,
    metadata: 'Solo 150 miembros · Por invitación',
  },
  {
    id: 30,
    type: 'radio-live',
    title: 'Banda Radio MX está al aire con Tardes de Banda',
    message: 'Arrancó "Tardes de Banda" con los mejores éxitos regionales mexicanos. Mandá tu saludo y pedí tu canción favorita. ¡Que suene la banda!',
    preview: 'Banda Radio MX en vivo: Tardes de Banda, pedí tu canción.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20Mexican%20banda%20music%20radio%20studio%20with%20traditional%20instruments%20decor%20colorful%20papel%20picado%20warm%20festive%20lighting%20energetic%20atmosphere%20professional%20broadcast%20equipment%20celebration%20vibe&width=800&height=450&seq=notif-ctr-thumb-banda&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Mexican%20banda%20music%20radio%20station%20logo%20with%20trumpet%20and%20accordion%20circular%20design%20red%20white%20and%20green%20colors%20festive%20broadcast%20emblem%20traditional%20Mexican%20vibe%20isolated%20on%20transparent%20background%20vector%20flat%20design&width=120&height=120&seq=notif-ctr-icon-banda&orientation=squarish',
    timestamp: '2026-06-14T15:00:00',
    timestampRelative: 'Hace 1 semana',
    isRead: true,
    isPinned: false,
    actionUrl: '/radio/banda-radio-mx',
    actionLabel: 'Sintonizar',
    sourceName: 'Banda Radio MX',
    sourceFreq: '92.3 FM',
    category: 'actividad',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: '4,500 oyentes · 23 saludos enviados',
  },
  {
    id: 31,
    type: 'nuevo-evento',
    title: 'Taller de Locución Profesional — Últimos cupos',
    message: 'Solo quedan 5 cupos para el taller presencial de locución profesional con Carlos Herrera. Técnicas de voz, respiración y presencia al micrófono.',
    preview: 'Últimos cupos: taller de locución con Carlos Herrera.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Professional%20voice%20coaching%20workshop%20small%20intimate%20group%20setting%20experienced%20instructor%20demonstrating%20microphone%20technique%20focused%20students%20warm%20studio%20lighting%20educational%20engaged%20atmosphere&width=800&height=450&seq=notif-ctr-thumb-taller&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20industrial%20modern%20style%20metallic%20warm%20tones%20professional%20broadcasting%20emblem&width=120&height=120&seq=notif-ctr-icon-industria2&orientation=squarish',
    timestamp: '2026-06-14T11:00:00',
    timestampRelative: 'Hace 1 semana',
    isRead: true,
    isPinned: false,
    actionUrl: '/eventos',
    actionLabel: 'Reservar cupo',
    sourceName: 'Radio Industria',
    sourceFreq: '93.5 FM',
    category: 'eventos',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: 'Solo 5 cupos · Sábado 21 junio · Presencial',
  },
  {
    id: 32,
    type: 'programa-iniciado',
    title: 'Noches de Jazz está comenzando',
    message: 'El refugio sonoro de las medianoches arranca en Estéreo Capital. Hoy: especial Miles Davis en el aniversario de Kind of Blue. Una hora de jazz puro.',
    preview: 'Noches de Jazz comienza en Estéreo Capital: especial Miles Davis.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Intimate%20jazz%20radio%20studio%20at%20night%20warm%20amber%20lighting%20vintage%20microphone%20saxophone%20in%20background%20cozy%20sophisticated%20atmosphere%20vinyl%20records%20visible%20moody%20elegant%20aesthetic&width=800&height=450&seq=notif-ctr-thumb-jazznocturno&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20warm%20earthy%20tones%20professional%20broadcasting%20emblem%20contemporary%20minimalist%20design&width=120&height=120&seq=notif-ctr-icon-capital4&orientation=squarish',
    timestamp: '2026-06-13T22:00:00',
    timestampRelative: 'Hace 1 semana',
    isRead: true,
    isPinned: false,
    actionUrl: '/radio/estereo-capital',
    actionLabel: 'Escuchar',
    sourceName: 'Estéreo Capital',
    sourceFreq: '102.1 FM',
    category: 'actividad',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: '980 oyentes · Especial Miles Davis',
  },
  {
    id: 33,
    type: 'nuevo-comentario',
    title: 'Andrea Ruiz mencionó tu radio en un post',
    message: '"Descubrí Radio Peninsular gracias a la recomendación de Marco y es INCREÍBLE. La mejor música tropical y los locutores son lo más." — Andrea Ruiz',
    preview: 'Andrea Ruiz recomendó tu radio en su post.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Friendly%20latina%20girl%20mid%20twenties%20casual%20style%20genuine%20warm%20smile%20portrait%20photography%20soft%20natural%20lighting%20tropical%20vibe%20clean%20background%20editorial%20quality&width=800&height=450&seq=notif-ctr-avatar-andrea&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Friendly%20latina%20girl%20profile%20photo%20casual%20professional%20warm%20natural%20lighting%20half%20body%20portrait%20clean%20background%20editorial%20quality&width=120&height=120&seq=notif-ctr-icon-andrea&orientation=squarish',
    timestamp: '2026-06-13T16:00:00',
    timestampRelative: 'Hace 1 semana',
    isRead: true,
    isPinned: false,
    actionUrl: '/listener',
    actionLabel: 'Ver mención',
    sourceName: 'Andrea Ruiz',
    sourceFreq: '',
    category: 'social',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: '45 likes · 12 compartidos',
  },
  {
    id: 34,
    type: 'nuevo-seguidor',
    title: 'Radio Élite Digital comenzó a seguirte',
    message: 'La emisora digital más grande de la plataforma ahora te sigue. ¡Tu contenido está siendo reconocido a nivel industria! Esto es enorme.',
    preview: 'Radio Élite Digital, la emisora top, te sigue ahora.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20building%20exterior%20with%20illuminated%20signage%20at%20dusk%20professional%20broadcast%20facility%20architectural%20photography%20urban%20setting%20clean%20contemporary%20design%20impressive%20scale&width=800&height=450&seq=notif-ctr-avatar-elite&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20modern%20circular%20minimalist%20design%20warm%20golden%20accents%20on%20dark%20background%20professional%20broadcasting%20emblem%20sleek%20contemporary&width=120&height=120&seq=notif-ctr-icon-elite&orientation=squarish',
    timestamp: '2026-06-12T12:00:00',
    timestampRelative: 'Hace 1 semana',
    isRead: false,
    isPinned: false,
    actionUrl: '/seguidores',
    actionLabel: 'Ver perfil',
    sourceName: 'Radio Élite Digital',
    sourceFreq: '99.1 FM',
    category: 'social',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: '850K oyentes mensuales · Emisora top',
  },
  {
    id: 35,
    type: 'nuevo-concurso',
    title: 'Crea tu Jingles 2026 — Nuevas categorías abiertas',
    message: 'Radio Peninsular anunció 2 nuevas categorías para Crea tu Jingles 2026: Mejor Jingles Instrumental y Mejor Jingles Vocal. $5,000 adicionales en premios.',
    preview: 'Nuevas categorías en Crea tu Jingles 2026: $5,000 más en premios.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Creative%20music%20competition%20stage%20with%20colorful%20jingles%20display%20microphones%20and%20awards%20professional%20studio%20atmosphere%20warm%20spotlight%20artistic%20composition%20award%20ceremony%20photography%20excitement&width=800&height=450&seq=notif-ctr-thumb-jingles&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20coastal%20inspired%20blue%20and%20warm%20sand%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=notif-ctr-icon-peninsular3&orientation=squarish',
    timestamp: '2026-06-12T10:00:00',
    timestampRelative: 'Hace 1 semana',
    isRead: true,
    isPinned: false,
    actionUrl: '/competencias',
    actionLabel: 'Ver categorías',
    sourceName: 'Radio Peninsular',
    sourceFreq: '101.9 FM',
    category: 'concursos',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: '6 categorías · $12,000 en premios totales',
  },
  {
    id: 36,
    type: 'invitacion-club',
    title: 'Invitación al Club de Fans de Radio Deportiva Total',
    message: 'Como fanático del deporte, te invitamos al Club Oficial de Radio Deportiva Total. Accedé a estadísticas exclusivas, participá en predicciones y ganá premios deportivos.',
    preview: 'Club de Fans de Radio Deportiva Total te invita a unirte.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Passionate%20sports%20radio%20fan%20club%20watching%20match%20together%20on%20big%20screen%20cheering%20celebration%20moment%20dynamic%20atmosphere%20team%20jerseys%20and%20scarves%20sports%20bar%20setting%20professional%20photography&width=800&height=450&seq=notif-ctr-thumb-club-deportes&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20trophy%20icon%20circular%20design%20dark%20blue%20and%20gold%20color%20scheme%20dynamic%20broadcast%20emblem%20energetic%20sports%20feel%20isolated%20on%20transparent%20background%20vector%20flat%20design&width=120&height=120&seq=notif-ctr-icon-deportes3&orientation=squarish',
    timestamp: '2026-06-11T19:00:00',
    timestampRelative: 'Hace 10 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/clubes',
    actionLabel: 'Unirme al club',
    sourceName: 'Radio Deportiva Total',
    sourceFreq: '95.7 FM',
    category: 'clubes',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: '5,600 miembros · Predicciones y premios',
  },
  {
    id: 37,
    type: 'nuevo-ganador',
    title: 'Ganador del concurso de canto de La Kalle 99.9',
    message: '¡Tenemos ganador! Luis Fernando Gómez se llevó el primer lugar con su interpretación de balada pop. Escuchá su presentación y los comentarios del jurado.',
    preview: 'Luis Fernando Gómez ganó el concurso de canto de La Kalle 99.9.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Young%20male%20singer%20holding%20trophy%20on%20stage%20spotlight%20dramatic%20moment%20golden%20confetti%20raining%20down%20proud%20expression%20professional%20event%20photography%20winner%20celebration%20concert%20atmosphere&width=800&height=450&seq=notif-ctr-thumb-kalle&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Pop%20music%20radio%20station%20logo%20with%20headphones%20and%20music%20equalizer%20bars%20circular%20design%20vibrant%20pink%20and%20coral%20colors%20modern%20youthful%20broadcast%20emblem%20energetic%20style%20isolated%20on%20transparent%20background%20vector%20flat%20design&width=120&height=120&seq=notif-ctr-icon-kalle&orientation=squarish',
    timestamp: '2026-06-11T16:00:00',
    timestampRelative: 'Hace 10 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/ganadores',
    actionLabel: 'Escuchar ganador',
    sourceName: 'La Kalle 99.9',
    sourceFreq: '99.9 FM',
    category: 'concursos',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: '345 participantes · Premio: $1,500',
  },
  {
    id: 38,
    type: 'nuevo-evento',
    title: 'Gala de Locutores — Transmisión en vivo confirmada',
    message: 'La Gala de Locutores será transmitida en vivo por RFW TV. Alfombra roja, entrevistas exclusivas y toda la emoción de la noche más importante de la radio.',
    preview: 'Gala de Locutores: transmisión en vivo por RFW TV.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Elegant%20awards%20gala%20red%20carpet%20event%20glamorous%20atmosphere%20golden%20lighting%20TV%20cameras%20broadcasting%20live%20sophisticated%20venue%20professional%20event%20photography%20celebrities%20and%20hosts&width=800&height=450&seq=notif-ctr-thumb-gala&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Official%20RFW%20TV%20broadcast%20logo%20professional%20modern%20circular%20design%20red%20carpet%20gold%20and%20black%20accents%20premium%20event%20branding&width=120&height=120&seq=notif-ctr-icon-rfwtv&orientation=squarish',
    timestamp: '2026-06-10T14:00:00',
    timestampRelative: 'Hace 11 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/eventos',
    actionLabel: 'Ver detalles',
    sourceName: 'RFW Oficial',
    sourceFreq: 'Global',
    category: 'eventos',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: '15 de agosto · Teatro Municipal · Transmisión en vivo',
  },
  {
    id: 39,
    type: 'programa-iniciado',
    title: 'Sábados de Rock edición aniversario está al aire',
    message: 'Hoy se cumplen 2 años de Sábados de Rock en Radio Peninsular. 4 horas especiales con los mejores momentos de estos 2 años, invitados sorpresa y sorteos.',
    preview: 'Sábados de Rock: edición aniversario 2 años en Radio Peninsular.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20anniversary%20celebration%20in%20studio%20with%20balloons%20cake%20guitars%20and%20enthusiastic%20hosts%20party%20atmosphere%20warm%20lighting%20genuine%20celebration%20professional%20photography%20fun%20vibrant&width=800&height=450&seq=notif-ctr-thumb-rock-aniv&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20coastal%20inspired%20blue%20and%20warm%20sand%20tones%20professional%20broadcasting%20emblem%20contemporary%20design&width=120&height=120&seq=notif-ctr-icon-peninsular4&orientation=squarish',
    timestamp: '2026-06-10T16:00:00',
    timestampRelative: 'Hace 11 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/radio/radio-peninsular',
    actionLabel: 'Sintonizar',
    sourceName: 'Radio Peninsular',
    sourceFreq: '101.9 FM',
    category: 'actividad',
    priority: 'alta',
    canMarkRead: true,
    canDelete: true,
    metadata: 'Aniversario 2 años · 4 horas especiales',
  },
  {
    id: 40,
    type: 'locutor-live',
    title: 'Carlos Herrera está al aire con su programa',
    message: 'El hombre de las mil voces arranca su programa en Radio Industria. Hoy haciendo voces de personajes en vivo. Imperdible.',
    preview: 'Carlos Herrera al aire en Radio Industria con sus mil voces.',
    thumbnailUrl: 'https://readdy.ai/api/search-image?query=Versatile%20male%20voice%20actor%20and%20radio%20host%20in%20professional%20recording%20booth%20with%20script%20performing%20multiple%20character%20voices%20dynamic%20creative%20energy%20expressive%20face%20warm%20studio%20lighting&width=800&height=450&seq=notif-ctr-thumb-carlos-h&orientation=landscape',
    iconUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20logo%20circular%20industrial%20modern%20style%20metallic%20warm%20tones%20professional%20broadcasting%20emblem&width=120&height=120&seq=notif-ctr-icon-industria3&orientation=squarish',
    timestamp: '2026-06-10T10:00:00',
    timestampRelative: 'Hace 11 días',
    isRead: true,
    isPinned: false,
    actionUrl: '/radio/radio-industria',
    actionLabel: 'Escuchar ahora',
    sourceName: 'Radio Industria',
    sourceFreq: '93.5 FM',
    category: 'actividad',
    priority: 'normal',
    canMarkRead: true,
    canDelete: true,
    metadata: '1,230 oyentes · Voces en vivo',
  },
];

export const notificationDashboardStats: NotificationDashboardStats = {
  totalNotificaciones: 52340,
  totalHoy: 24,
  totalSinLeer: 8,
  masFrecuente: 'Nuevo Comentario',
  masFrecuenteCount: 14200,
  tasaApertura: 92.5,
  tiempoPromedioAccion: '2.4 min',
  notificacionesPorDia: 18,
};

export const notificationActions: NotificationActionsInfo[] = [
  {
    id: 'ver',
    name: 'Ver',
    icon: 'ri-eye-line',
    description: 'Abrí la notificación e interactuá directamente con el contenido.',
    color: 'text-sky-600',
    shortcut: 'Click',
  },
  {
    id: 'leido',
    name: 'Marcar Leído',
    icon: 'ri-check-double-line',
    description: 'Marcá la notificación como leída para mantener tu bandeja organizada.',
    color: 'text-emerald-600',
    shortcut: 'Doble click',
  },
  {
    id: 'eliminar',
    name: 'Eliminar',
    icon: 'ri-delete-bin-line',
    description: 'Eliminá notificaciones que ya no necesitás ver.',
    color: 'text-rose-600',
    shortcut: 'Icono X',
  },
  {
    id: 'configurar',
    name: 'Configurar',
    icon: 'ri-settings-3-line',
    description: 'Personalizá qué notificaciones querés recibir y de quién.',
    color: 'text-foreground-600',
    shortcut: 'Engranaje',
  },
];

export function getNotificationsByCategory(categoryId: NotificationCategoryId): NotificationItem[] {
  return notificationItems.filter((n) => n.category === categoryId);
}

export function getUnreadNotifications(): NotificationItem[] {
  return notificationItems.filter((n) => !n.isRead);
}

export function getNotificationsByType(typeId: NotificationTypeId): NotificationItem[] {
  return notificationItems.filter((n) => n.type === typeId);
}

export function getPinnedNotifications(): NotificationItem[] {
  return notificationItems.filter((n) => n.isPinned);
}

export function getRecentNotifications(limit = 6): NotificationItem[] {
  return [...notificationItems].filter((n) => !n.isRead).slice(0, limit);
}

export function getCategoryById(id: NotificationCategoryId): NotificationCategory | undefined {
  return notificationCategories.find((c) => c.id === id);
}

export function getTypeById(id: NotificationTypeId): NotificationType | undefined {
  return notificationTypes.find((t) => t.id === id);
}